import {EnumPeriodicidad} from "../../../core/models/enum/enum-programacion-obra";
import {ClsCalendario} from "../../../core/models/cls-catalogos";
import {ClsUtil} from "../../../util/cls-util";
import {ClsPrograma} from "../../../core/models/cls-programa";
import {IFechasPeriodo} from "../../../core/models/cls-sobrecosto";


export class ClsFechasPeriodos {
  constructor() {
  }

  /**
   * Normaliza a Date solo día/mes/año en hora local.
   * Acepta Date, ISO string (p. ej. desde API/JSON) o timestamp.
   */
  public static soloFecha(fecha: Date | string | number | null | undefined): Date {
    if (fecha == null || fecha === '') {
      throw new Error('Fecha inválida');
    }
    const d = fecha instanceof Date ? fecha : new Date(fecha);
    if (isNaN(d.getTime())) {
      throw new Error('Fecha inválida');
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /**
   *
   * @param periodicidad
   * @param fechaInicio
   * @param fechaFinal
   * @returns regresa el numero total de periodos segun la periodicidad.
   */
  public calcularPeriodo(periodicidad: EnumPeriodicidad, fechaInicio: Date, fechaFinal: Date): number {
    fechaInicio.setHours(0, 0, 0, 0);
    fechaFinal.setHours(0, 0, 0, 0);
    switch (periodicidad) {
      case EnumPeriodicidad.MENSUAL:
        return this.calcularPeriodicidadMensual(fechaInicio, fechaFinal);
      case EnumPeriodicidad.QUINCENAL:
        return this.calcularPeriodicidadQuincenal(fechaInicio, fechaFinal);
      case EnumPeriodicidad.SEMANAL:
        return this.calcularPeriodicidadSemanal(fechaInicio, fechaFinal)
      case EnumPeriodicidad.DIARIA:
        return this.calcularDiasCalendario(fechaInicio, fechaFinal);
    }
  }

  public calcularDiasHabiles(fechaInicial: Date, fechaFinal: Date, calendario: ClsCalendario) {
    fechaInicial.setHours(0, 0, 0, 0);
    fechaFinal.setHours(0, 0, 0, 0);

    let contador = 0;
    let fechaActual = new Date(fechaInicial);

    // Obtener las fechas no laborables en un Set para búsqueda rápida
    const fechasNoLaborables = new Set((calendario.Fechas || []).map(f => new Date(f.FechaNoLaborable).toDateString()));

    while (fechaActual <= fechaFinal) {
      const diaSemana = fechaActual.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado

      const esDiaHabil =
        (diaSemana === 0 && calendario.Domingo) ||
        (diaSemana === 1 && calendario.Lunes) ||
        (diaSemana === 2 && calendario.Martes) ||
        (diaSemana === 3 && calendario.Miercoles) ||
        (diaSemana === 4 && calendario.Jueves) ||
        (diaSemana === 5 && calendario.Viernes) ||
        (diaSemana === 6 && calendario.Sabado);

      const esFechaNoLaborable = fechasNoLaborables.has(fechaActual.toDateString());

      if (esDiaHabil && !esFechaNoLaborable) {
        contador++;
      }

      fechaActual.setDate(fechaActual.getDate() + 1);
    }

    return contador;
  }

  public calcularDiasCalendario(fechaInicial: Date, fechaFinal: Date): number {
    fechaInicial.setHours(0, 0, 0, 0);
    fechaFinal.setHours(0, 0, 0, 0);
    return ClsUtil.obtenerNumDiferenciaFechaEntreFecha(fechaInicial, fechaFinal);
  }

  private calcularPeriodicidadMensual(fechaInicio: Date, fechaFinal: Date): number {
    let tFecha = new Date(fechaInicio);
    let tMes = tFecha.getMonth();
    let totMeses = 1;

    while (tFecha <= fechaFinal) {
      if (tMes !== tFecha.getMonth()) {
        totMeses++;
        tMes = tFecha.getMonth();
      }
      tFecha.setDate(tFecha.getDate() + 1);
    }

    return totMeses;
  }

  private calcularPeriodicidadQuincenal(fechaInicio: Date, fechaFinal: Date): number {
    let tFecha = new Date(fechaInicio);
    let totMeses = 0;

    while (tFecha <= fechaFinal) {
      if (tFecha.getDate() === 15 || tFecha.getDate() === 1) {
        // Es fin de quincena
        totMeses++;
      }
      tFecha.setDate(tFecha.getDate() + 1);
    }

    // Agrega una quincena más si no cayó en el último día de quincena
    if (fechaFinal.getDate() !== 15 && fechaFinal.getDate() !== 1) {
      totMeses++;
    }
    return totMeses;
  }

  private calcularPeriodicidadSemanal(fechaInicio: Date, fechaFinal: Date): number {
    let tFecha = new Date(fechaInicio);
    let totMeses = 0;

    while (tFecha <= fechaFinal) {
      if (tFecha.getDay() === 0) {
        // Es un domingo
        totMeses++;
      }
      tFecha.setDate(tFecha.getDate() + 1);
    }

    // Agrega una semana más si no cayó en domingo el último día
    if (fechaFinal.getDay() !== 0) {
      totMeses++;
    }

    return totMeses;
  }

  /**
   * Calcula las fechas inicio/fin de cada periodo según programa (equivalente VB ObtieneFechasPeriodo).
   * Periodicidad: 1=Mes, 2=Quincena, 3=Semana, 4=Día. Se reparte el rango FechaInicio..FechaFinal en NoPeriodos intervalos.
   */
  public calcularFechasPeriodo(programa: ClsPrograma): Map<number, IFechasPeriodo>  {

    let fechasPeriodos = new Map<number, IFechasPeriodo>();

    /** Solo fecha local (sin hora); evita que el mismo día con distinta hora rompa fecPas <= fechaFinal. */
    const fechaInicioPrograma = ClsFechasPeriodos.soloFecha(new Date(programa.FechaInicio));
    const fechaFinalPrograma = ClsFechasPeriodos.soloFecha(new Date(programa.FechaFinal));

    const noPeriodos = programa.NoPeriodos;
    const periodicidad = Math.floor(Number(programa.Periodicidad)) || 1;

    const devuelveUltimoDiaMes = (f: Date) =>
      new Date(f.getFullYear(), f.getMonth() + 1, 0);

    fechasPeriodos.clear();

    let fecPas = new Date(fechaInicioPrograma.getTime());
    let i = 0;

    switch (periodicidad) {
      case EnumPeriodicidad.MENSUAL: // Mensual
        while (fecPas <= fechaFinalPrograma) {
          // Verificar si mañana es día 1 (es el último día del mes)
          const maniana = new Date(fecPas);
          maniana.setDate(maniana.getDate() + 1);

          if (maniana.getDate() === 1) {
            i++;
            if (i <= noPeriodos) {
              fechasPeriodos.set(i, {
                fechaInicio: new Date(fecPas.getFullYear(), fecPas.getMonth(), 1),
                fechaFinal: new Date(fecPas)
              });
            }
          }
          fecPas.setDate(fecPas.getDate() + 1);
        }
        // Validar si el último día del programa no cayó en fin de mes
        let ultimoDiaMes = new Date(fechaFinalPrograma);
        ultimoDiaMes.setDate(ultimoDiaMes.getDate() + 1);
        if (ultimoDiaMes.getDate() !== 1) {
          i++;
          if (i <= noPeriodos) {
            fechasPeriodos.set(i, {
              fechaInicio: new Date(fechaFinalPrograma.getFullYear(), fechaFinalPrograma.getMonth(), 1),
              fechaFinal: devuelveUltimoDiaMes(fechaFinalPrograma)
            });
          }
        }
        break;

      case EnumPeriodicidad.QUINCENAL: // Quincenal
        while (fecPas <= fechaFinalPrograma) {
          const maniana = new Date(fecPas);
          maniana.setDate(maniana.getDate() + 1);

          if (maniana.getDate() === 1 || fecPas.getDate() === 15) {
            i++;
            if (i <= noPeriodos) {
              let inicio: Date;
              if (fecPas.getDate() === 15) {
                inicio = new Date(fecPas.getFullYear(), fecPas.getMonth(), 1);
              } else {
                inicio = new Date(fecPas.getFullYear(), fecPas.getMonth(), 16);
              }
              fechasPeriodos.set(i, { fechaInicio: inicio, fechaFinal: new Date(fecPas) });
            }
          }
          fecPas.setDate(fecPas.getDate() + 1);
        }
        // Validar cierre de quincena incompleta
        if (fechaFinalPrograma.getDate() !== 15 && (new Date(new Date(fechaFinalPrograma).setDate(fechaFinalPrograma.getDate() + 1)).getDate() !== 1)) {
          i++;
          if (i <= noPeriodos) {
            let inicio: Date, fin: Date;
            if (fechaFinalPrograma.getDate() <= 15) {
              inicio = new Date(fechaFinalPrograma.getFullYear(), fechaFinalPrograma.getMonth(), 1);
              fin = new Date(fechaFinalPrograma.getFullYear(), fechaFinalPrograma.getMonth(), 15);
            } else {
              inicio = new Date(fechaFinalPrograma.getFullYear(), fechaFinalPrograma.getMonth(), 16);
              fin = devuelveUltimoDiaMes(fechaFinalPrograma);
            }
            fechasPeriodos.set(i, { fechaInicio: inicio, fechaFinal: fin });
          }
        }
        break;

      case EnumPeriodicidad.SEMANAL: // Semanal (Corte en Domingo)
        while (fecPas <= fechaFinalPrograma) {
          if (fecPas.getDay() === 0) { // 0 es Domingo en JS
            i++;
            if (i <= noPeriodos) {
              let inicio = new Date(fecPas);
              inicio.setDate(inicio.getDate() - 6);
              fechasPeriodos.set(i, { fechaInicio: inicio, fechaFinal: new Date(fecPas) });
            }
          }
          fecPas.setDate(fecPas.getDate() + 1);
        }
        if (fechaFinalPrograma.getDay() !== 0) {
          i++;
          let fin = new Date(fechaFinalPrograma);
          // Ajustar al próximo domingo
          let diasParaDomingo = 7 - (fin.getDay() === 0 ? 7 : fin.getDay());
          fin.setDate(fin.getDate() + diasParaDomingo);
          if (i <= noPeriodos) {
            let inicio = new Date(fin);
            inicio.setDate(inicio.getDate() - 6);
            fechasPeriodos.set(i, { fechaInicio: inicio, fechaFinal: fin });
          }
        }
        break;

      case EnumPeriodicidad.DIARIA: // Diario
        for (let d = 1; d <= noPeriodos; d++) {
          let fecha = new Date(fechaInicioPrograma);
          fecha.setDate(fecha.getDate() + (d - 1));
          fechasPeriodos.set(d, { fechaInicio: fecha, fechaFinal: new Date(fecha) });
        }
        break;
    }

    return fechasPeriodos;
  }

  /** Mapa de periodos con duracionHabil precalculada (VB antes de GeneraPorcentajes). */
  public calcularFechasPeriodoConDuracion(programa: ClsPrograma, calendario: ClsCalendario | null): Map<number, IFechasPeriodo> {
    const mapa = this.calcularFechasPeriodo(programa);
    mapa.forEach((p, no) => {
      p.duracionHabil = this.calcularDuracionHabil(p.fechaInicio, p.fechaFinal, calendario);
      mapa.set(no, p);
    });
    return mapa;
  }

  public esDiaHabil(fecha: Date, calendario: ClsCalendario): boolean {
    const f = ClsFechasPeriodos.soloFecha(fecha);
    const dia = f.getDay();
    const fechasNoLab = new Set((calendario.Fechas ?? []).map(x => new Date(x.FechaNoLaborable).toDateString()));
    const diaSemanaOk =
      (dia === 0 && calendario.Domingo) ||
      (dia === 1 && calendario.Lunes) ||
      (dia === 2 && calendario.Martes) ||
      (dia === 3 && calendario.Miercoles) ||
      (dia === 4 && calendario.Jueves) ||
      (dia === 5 && calendario.Viernes) ||
      (dia === 6 && calendario.Sabado);
    return diaSemanaOk && !fechasNoLab.has(f.toDateString());
  }

  public siguienteDiaHabil(fecha: Date, calendario: ClsCalendario | null): Date {
    if (!calendario) {
      return ClsFechasPeriodos.soloFecha(fecha);
    }
    let f = ClsFechasPeriodos.soloFecha(fecha);
    for (let i = 0; i < 366 * 5; i++) {
      if (this.esDiaHabil(f, calendario)) {
        return f;
      }
      f = new Date(f.getFullYear(), f.getMonth(), f.getDate() + 1);
    }
    return f;
  }

  public calcularFechaFinalDesdeDuracion(fechaInicio: Date, duracion: number, calendario: ClsCalendario | null): Date {
    if (duracion < 1) {
      duracion = 1;
    }
    if (!calendario) {
      const ini = ClsFechasPeriodos.soloFecha(fechaInicio);
      const fin = new Date(ini);
      fin.setDate(fin.getDate() + duracion - 1);
      return fin;
    }
    let f = this.siguienteDiaHabil(fechaInicio, calendario);
    let cont = 1;
    while (cont < duracion) {
      f = new Date(f.getFullYear(), f.getMonth(), f.getDate() + 1);
      if (this.esDiaHabil(f, calendario)) {
        cont++;
      }
    }
    return f;
  }

  public calcularDuracionHabil(inicio: Date, fin: Date, calendario: ClsCalendario | null): number {
    const i = ClsFechasPeriodos.soloFecha(inicio);
    const t = ClsFechasPeriodos.soloFecha(fin);
    if (!calendario) {
      return this.calcularDiasCalendario(i, t);
    }
    return this.calcularDiasHabiles(i, t, calendario);
  }

  public fechaDentroDePeriodo(fecha: Date, periodo: IFechasPeriodo): boolean {
    const f = ClsFechasPeriodos.soloFecha(fecha);
    const pi = ClsFechasPeriodos.soloFecha(periodo.fechaInicio);
    const pf = ClsFechasPeriodos.soloFecha(periodo.fechaFinal);
    return f >= pi && f <= pf;
  }
}
